/* eslint-disable no-underscore-dangle, */
import React from 'react';
import gsap from 'gsap';
import classNames from 'classnames';

import { resizeModule } from '@shcc/modules/ResizeModule';
import { scrollModule } from '@shcc/modules/ScrollModule';
import { scrollContainerModule } from '@shcc/components/ScrollContainerComponent/ScrollContainerComponent';

// Overlays
import OverlaySidebar from '@shcc/overlays/OverlaySidebar/OverlaySidebar';

// Utils
import bindAll from '@shcc/utils/helpers/bindAll';
import clamp from '@shcc/utils/helpers/clamp';
import { lerp } from '@shcc/modules/StandardEaseModule';
import fixed from '@shcc/utils/helpers/fixed';

// New
import { withMainNavigation } from '@hooks';
import MessageBanner from '@common/components/MessageBanner/MessageBanner';
import { List } from '@components/navigations/MainNavigation/List';
import { ButtonHamburger } from '@components/navigations/MainNavigation/ButtonHamburger';
import { Logo, LOGO_THEME } from '@components/navigations/MainNavigation/Logo';
import { LanguageSwitch } from '@components/navigations/SidebarNavigation/LanguageSwitch';

const DISALLOWED_SCROLL_CONTAINERS = [
  'air/connectivity',
  'air/exterior',
  'air/interior',
  'air/performance',
  'air/reserve',
  'air/reserve/thank-you',
  'air/safety',
  'air/specs',
  'sidebar',
  'video',
];

// Styles
import styles from './MainNavigationComponent.module.scss';

class MainNavigationComponent extends React.Component {
  constructor() {
    super();

    // Bind handler
    bindAll(this, [
      '_tickHandler',
      '_scrollHandler',
      '_resizeHandler',
      '_trottlerCompleteHandler',
      '_buttonHamburgerClickHandler',
      '_scrollChangeDirectionlHandler',
      '_setNavPosition',
      'animate',
    ]);

    this._offset = {};
    this._offset.start = scrollModule.position.y;
    this._offset.current = scrollModule.position.y;
    this._offset.end = scrollModule.position.y;

    this._position = 0;
    this._alpha = 0;
    this._blur = false;

    // UI
    this.element = React.createRef();

    this.ui = {};
    this.ui.background = React.createRef();
    this.state = {
      theme: 'white',
      scrollInvert: true,
      showTop: true,
      navScrolled: false,
      scrollPos: 0,
      isSidebarOpen: false,
    };
  }

  componentDidMount() {
    this._setupEventListeners();
    // this._setupTrottler();

    this._setDimensions();

    if (typeof document !== `undefined`) {
      this.setState({
        theme: document.body.classList.contains('page-light')
          ? 'black'
          : 'white',
      });
    }
  }

  componentWillUnmount() {
    this._removeEventListeners();
  }

  render() {
    const {
      columns,
      className,
      type,
      loggedIn,
      invertibleLinkTheme = false,
      user,
    } = this.props || {};
    return (
      <>
        <MessageBanner />
        <nav
          className={classNames([
            className,
            styles.container,
            styles[this.state.theme],
            type === 'portal' && styles.portal,
            type === 'portal' && this.state.showTop === false && styles.hidden,
            type === 'portal' && loggedIn === true
              ? styles.loggedIn
              : type === 'portal' && loggedIn === false
              ? styles.loggedOut
              : '',
            this.props.hideReserve ? styles.reserveMobile : '',
          ])}
          ref={this.element}
        >
          <span className={styles.background} ref={this.ui.background}></span>
          <List
            items={columns[0]}
            invertTheme={invertibleLinkTheme && !this.state.scrollInvert}
          />
          <Logo
            className={classNames([
              styles.logo,
              this.props.hideReserve ? '' : styles.logoMobile,
            ])} // logo link class
            // imgClassName={this.props.hideReserve && !this.state.scrollInvert ? styles.reserveMobile : ''} // logo img class
            alt="logo"
            theme={
              invertibleLinkTheme && !this.state.scrollInvert
                ? LOGO_THEME.dark
                : LOGO_THEME.light
            }
            onClick={e=>window.reload()}
          />
          <List
            items={this.props.hideReserve ? [] : columns[1]}
            location={this.props.location}
            user={user}
            renderToggle={() => (
              <section className={styles.hamburgerWrap}>
              {this.props.hideReserve && (<div className={styles.langSwitch}><LanguageSwitch /></div>)}
              <ButtonHamburger
                onClick={this._buttonHamburgerClickHandler}
                themeDark={this.props.hideReserve && !this.state.scrollInvert}
              />
            </section>
            )}
          />
          {this.state.isSidebarOpen && (
            <OverlaySidebar
              closeSidebarHandler={this.closeSidebarHandler}
              isSidebarOpen={this.state.isSidebarOpen}
            />
          )}
        </nav>
      </>
    );
  }

  _setupEventListeners() {
    gsap.ticker.add(this._tickHandler);
    scrollModule.addEventListener('scroll', this._scrollHandler);
    scrollModule.addEventListener(
      'change:direction',
      this._scrollChangeDirectionlHandler
    );
    resizeModule.addEventListener('resize', this._resizeHandler);
    resizeModule.addEventListener('resize:complete', this._resizeHandler);
  }

  _removeEventListeners() {
    gsap.ticker.remove(this._tickHandler);
    scrollModule.removeEventListener('scroll', this._scrollHandler);
    resizeModule.removeEventListener('resize', this._resizeHandler);
    resizeModule.removeEventListener('resize:complete', this._resizeHandler);
  }

  _setDimensions() {
    this._dimensions = {};
    this._dimensions.height = this.element.current.clientHeight;
    this._dimensions.width = this.element.current.clientWidth;
  }

  // prettier-ignore
  _setPosition() {
    const { y: pageOffsetYAxis } = scrollModule.position;
    const difference = Math.abs(this._position - this._offset.current);
    const position = fixed(lerp(this._position, this._offset.current, 0.095), 2);

    if (pageOffsetYAxis < 75) {
      this._position = position;
      this.element.current.style.transform = `matrix(1.0, 0.0, 0.0, 1.0, 0, 0)`;
    } else if (position !== this._position) {
      this._position = position;
      this.element.current.style.transform = `matrix(1.0, 0.0, 0.0, 1.0, 0, ${-this._position})`;
    }
  }

  _setAlpha() {
    const { position } = scrollModule;
    const { y } = position;
    const progress = clamp(y, 0, 200) / 200;

    // Set opacity
    this._alpha = lerp(this._alpha, progress, 0.15);
    this.ui.background.current.style.opacity = this._alpha;

    if (!this._blur && progress === 1) {
      this._blur = true;
      //this.setState({ scrollInvert: true });
      this.element.current.style.backdropFilter = 'blur(10px)';
    }

    if (this._blur && progress < 1) {
      this._blur = false;
      //this.setState({ scrollInvert: true });
      this.element.current.style.backdropFilter = 'none';
    }

    // const { viewportHeight } = resizeModule;
    // const { direction, position } = scrollModule;
    // const { y } = position;
    // const { height } = this._dimensions;
    // const target = clamp((1 / height) * (y - viewportHeight), 0.0, 1.0);
    // const alphaDiff = Math.abs(this._alpha - target);
    // const alpha = alphaDiff < 0.01 ? target : lerp(this._alpha, target, 0.095);
    // if (alpha !== this._alpha && direction === SCROLL_DIRECTION.up) {
    //   this._alpha = alpha;
    //   this.ui.background.current.style.opacity = this._alpha;
    // }
  }

  /**
   *
   * Handlers
   */

  closeSidebarHandler = () => {
    this.setState({
      isSidebarOpen: false,
    });
  };

  _buttonHamburgerClickHandler = () => {
    this.setState({
      isSidebarOpen: true,
    });
  };

  _tickHandler() {
    if (
      DISALLOWED_SCROLL_CONTAINERS.indexOf(scrollContainerModule.active) === -1
    ) {
      // this._setPosition();
      this._setAlpha();
    }
  }

  _setNavPosition() {
    const el = this.element.current,
      cl = styles.fixed,
      mb = el.previousSibling,
      pos = mb ? mb.getBoundingClientRect().bottom < 0 : true,
      neg = mb ? mb.getBoundingClientRect().bottom > 0 : false,
      state = el.classList.contains(cl);
    if (pos && !state) {
      el.classList.add(cl);
    } else if (neg && state) {
      el.classList.remove(cl);
    }
  }

  animate() {
    this._scrollHandler();
    requestAnimationFrame(this.animate);
  }

  _scrollHandler() {
    const { scrollPos } = this.state;
    const { type } = this.props;
    if (type === 'portal') {
      this.setState({
        scrollPos: document.body.getBoundingClientRect().top,
        showTop: document.body.getBoundingClientRect().top > scrollPos,
        navScrolled: scrollPos < 0,
      });
    }

    if (!this._isScrolling) {
      this._isScrolling = true;
      this._offset.start = scrollModule.position.y;
    }

    const { end, start } = this._offset;
    const { y } = scrollModule.position;

    this._setNavPosition();

    // Elastic scroll
    if (y > 0) {
      this._offset.current = clamp(
        end + (y - start),
        0,
        this._dimensions.height
      );
    }
  }

  _resizeHandler() {
    this._setDimensions();
  }

  _scrollChangeDirectionlHandler(direction) {
    this._isScrolling = false;
    this._offset.end = this._offset.current;
  }
}

export default withMainNavigation(MainNavigationComponent);
